import React, { useState, useRef } from 'react';

const COND_ICONS = {'blessed':'🛡️','inspired':'✨','raging':'💪','hasted':'🔥','truesight':'👁️','shielded':'🫧','poisoned':'☠️','stunned':'💫','frozen':'❄️','bleeding':'🩸','restrained':'🔗','unconscious':'😴','frightened':'👻','nauseated':'🤢','silenced':'🔇','concentrating':'🎯','dodging':'🌙','paralyzed':'⛓️','incapacitated':'💤','blinded':'🙈','charmed':'💕','deafened':'🔇','exhausted':'😵','prone':'⬇️','weak venom':'🧪','strong venom':'🧪','deadly venom':'🧪','mindnumb toxin':'🧪','curse of silence':'🔇','necrotic rot':'🦠','buff:fire_shield':'🔥','buff:blur':'🌫️','buff:faerie_fire':'✨','buff:foresight':'👁️','buff:true_strike':'🎯','buff:hex':'🔮','buff:divine_favor':'⚔️','buff:agathys':'❄️'};
const BUFF_LIST = ['blessed','inspired','raging','hasted','truesight','shielded','concentrating','dodging','buff:fire_shield','buff:blur','buff:faerie_fire','buff:foresight','buff:true_strike','buff:hex','buff:divine_favor','buff:agathys'];

function getRarity(stats) {
  if (!stats) return '';
  const m = stats.match(/Artifact|Legendary|Very Rare|Uncommon|Rare|Common/);
  return m ? m[0] : '';
}

function getAtkMod(p, weaponStats) {
  const prof = p.level < 5 ? 2 : p.level < 9 ? 3 : p.level < 13 ? 4 : p.level < 17 ? 5 : 6;
  const stats = (weaponStats || '').toLowerCase();
  const useDex = stats.includes('finesse') || stats.includes('ranged') || stats.includes('light');
  const strMod = Math.floor(((p.strength || 10) - 10) / 2);
  const dexMod = Math.floor(((p.dexterity || 10) - 10) / 2);
  const abilMod = useDex ? Math.max(strMod, dexMod) : strMod;
  const total = abilMod + prof;
  return (total >= 0 ? '+' : '') + total;
}

function showItemDetail(stats, rarity) {
  if (!stats) return null;
  const parts = stats.split(' | ');
  let acLine = '', typeLine = '', rarityLine = '', statLines = [], passiveLines = [];
  parts.forEach(p => {
    if (p.startsWith('AC ')) acLine = p;
    else if (['Light','Medium','Heavy','Padded'].includes(p)) typeLine = p + ' Armor';
    else if (['Common','Uncommon','Rare','Very Rare','Legendary'].includes(p)) rarityLine = p;
    else if (p.startsWith('Resist:') || p.startsWith('+')) statLines.push(p);
    else passiveLines.push(p);
  });
  if (!acLine && parts.length >= 2 && parts[0].match(/\d+d\d+/)) {
    acLine = parts[0]; typeLine = parts[1]; rarityLine = parts[2] || '';
  }
  const rarityClass = rarity ? 'rarity-' + rarity.toLowerCase().replace(' ', '') : 'rarity-common';
  return { acLine, typeLine, rarityLine, statLines, passiveLines, rarityClass };
}

export default function PlayerCard({ player, isTurn, onTarget, onToggleActive, onKick, isDM }) {
  const [itemDetail, setItemDetail] = useState(null);
  const cardRef = useRef(null);
  const p = player;

  const hpPct = p.max_hp > 0 ? Math.round((p.hp / p.max_hp) * 100) : 100;
  const hpClass = hpPct > 60 ? 'hp' : hpPct > 30 ? 'hp mid' : 'hp low';
  const conditions = (p.conditions_active || '').split(',').filter(c => c.trim());
  const buffIcons = conditions.filter(c => BUFF_LIST.includes(c.trim().toLowerCase())).map(c => (
    <span key={c} title={c.trim()} style={{ fontSize: '0.7rem', cursor: 'help' }}>{COND_ICONS[c.trim().toLowerCase()] || '✨'}</span>
  ));
  const debuffIcons = conditions.filter(c => !BUFF_LIST.includes(c.trim().toLowerCase())).map(c => (
    <span key={c} title={c.trim()} style={{ fontSize: '0.7rem', cursor: 'help' }}>{COND_ICONS[c.trim().toLowerCase()] || '⚠️'}</span>
  ));

  const weapon = p.equipped_weapon || 'Unarmed';
  const isActive = p.active === 1;
  const border = isTurn ? '' : isActive ? 'var(--accent)' : '';
  const turnClass = isTurn ? ' active-turn' : '';

  const handleSlotClick = (e, name, stats) => {
    e.stopPropagation();
    if (!name) return;
    const rarity = getRarity(stats);
    const detail = showItemDetail(stats, rarity);
    setItemDetail({ name, rarity, ...detail });

    // Draw connector line
    const card = cardRef.current;
    if (!card) return;
    const oldSvg = card.querySelector('.slot-connector');
    if (oldSvg) oldSvg.remove();
    const clickedSlot = e.currentTarget;
    setTimeout(() => {
      const detailEl = card.querySelector('.item-detail');
      if (!detailEl || !clickedSlot) return;
      const cardRect = card.getBoundingClientRect();
      const slotRect = clickedSlot.getBoundingClientRect();
      const detailRect = detailEl.getBoundingClientRect();
      const x1 = slotRect.left + slotRect.width / 2 - cardRect.left;
      const y1 = slotRect.top + slotRect.height / 2 - cardRect.top;
      const isMobileView = window.innerWidth <= 1024;
      const x2 = isMobileView ? detailRect.left + detailRect.width / 2 - cardRect.left : detailRect.left - cardRect.left + 4;
      const y2 = isMobileView ? detailRect.bottom - cardRect.top : detailRect.top + detailRect.height / 2 - cardRect.top;
      const midX = x1 + (x2 - x1) * 0.3;
      const midY = isMobileView ? y1 + (y2 - y1) * 0.7 : y2 + 10;
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.classList.add('slot-connector');
      svg.style.cssText = 'position:absolute;top:0;left:0;width:100%;height:100%;pointer-events:none;z-index:15;overflow:visible;';
      svg.innerHTML = `<polyline points="${x1},${y1} ${midX},${midY} ${x2},${y2}" fill="none" stroke="#ff2222" stroke-width="1.5" stroke-dasharray="4,3" opacity="0.7"><animate attributeName="stroke-dashoffset" from="0" to="-14" dur="1s" repeatCount="indefinite"/></polyline><circle cx="${x1}" cy="${y1}" r="3" fill="#ff2222" opacity="0.8"/><circle cx="${x2}" cy="${y2}" r="2" fill="#ff2222" opacity="0.6"/>`;
      card.appendChild(svg);
    }, 50);
  };

  const slots = [
    { cls: 'slot-head', item: p.equipped_head, stats: p.equipped_head_stats },
    { cls: 'slot-chest', item: p.equipped_armor, stats: p.equipped_armor_stats },
    { cls: 'slot-hands', item: p.equipped_hands, stats: p.equipped_hands_stats },
    { cls: 'slot-ring', item: p.equipped_ring, stats: p.equipped_ring_stats },
    { cls: 'slot-feet', item: p.equipped_feet, stats: p.equipped_feet_stats },
  ];

  return (
    <div
      className={`player-card${turnClass}${!isActive ? ' inactive-card' : ''}`}
      style={{ borderColor: border }}
      id={`card-${p.discord_id}`}
      ref={cardRef}
      onClick={() => onTarget(p.char_name)}
    >
      <div className="body-diagram">
        <div className="body-img-container">
          <img src="body_model_transparent.png" className="body-img" alt="body" />
          {p.hp <= 0 && <div className="death-overlay">╳</div>}
          {slots.map(s => (
            <div
              key={s.cls}
              className={`slot-overlay ${s.cls} ${s.item ? 'equipped' : 'empty'}`}
              data-rarity={s.item ? getRarity(s.stats) : undefined}
              onClick={(e) => handleSlotClick(e, s.item, s.stats)}
            />
          ))}
        </div>
        <div className="weapon-slots">
          <div className={`weapon-slot ${p.equipped_weapon ? 'equipped' : 'empty'}`}>
            <span className="wep-icon">⚔️</span>
            <span className="wep-label">{weapon}</span>
            {p.equipped_weapon && <span className="wep-mod">({getAtkMod(p, p.equipped_weapon_stats)})</span>}
          </div>
          <div className={`weapon-slot ${p.equipped_offhand ? 'equipped' : 'empty'}`}>
            <span className="wep-icon">⚔️🛡️</span>
            <span className="wep-label">{p.equipped_offhand || 'OFF'}</span>
            {p.equipped_offhand && p.equipped_offhand !== 'shield' && <span className="wep-mod">({getAtkMod(p, p.equipped_offhand_stats)})</span>}
          </div>
        </div>
      </div>
      <div className="player-info">
        <div className="name">{p.char_name} {buffIcons}
          {isDM && onKick && <span className="kick-btn" title="Remove from room" onClick={(e) => { e.stopPropagation(); if(window.confirm(`Kick ${p.char_name} from the room?`)) onKick(p.discord_id); }}>✕</span>}
        </div>
        {isActive && <div className="active-beacon" onClick={(e) => { e.stopPropagation(); onToggleActive && onToggleActive(p.char_name); }} />}
        {!isActive && <div className="inactive-beacon" onClick={(e) => { e.stopPropagation(); onToggleActive && onToggleActive(p.char_name); }} />}
        <div className="class-race">Lv {p.level} {p.race} {p.class}{p.subclass ? ` (${p.subclass})` : ''}</div>
        <div className="bars">
          <div className="bar-row"><span className="label">HP</span><div className="bar"><div className={`fill ${hpClass}`} style={{ width: `${hpPct}%` }} /></div><span className="value">{p.hp} / {p.max_hp}</span></div>
          <div className="bar-row"><span className="label">AC</span><div className="bar"><div className="fill mp" style={{ width: `${Math.min(p.ac * 5, 100)}%` }} /></div><span className="value">{p.ac}</span></div>
        </div>
        <div className="bar-row"><span className="label">XP</span><div className="bar"><div className="fill xp" style={{ width: `${(() => { const thresholds = {1:0,2:300,3:900,4:2700,5:6500,6:14000,7:23000,8:34000,9:48000,10:64000,11:85000,12:100000,13:120000,14:140000,15:165000,16:195000,17:225000,18:265000,19:305000,20:355000}; const lv = p.level || 1; const curr = thresholds[lv] || 0; const next = thresholds[lv+1] || curr; if (next === curr) return 100; return Math.min(100, ((p.xp||0) - curr) / (next - curr) * 100); })()}%` }} /></div><span className="value">{p.xp || 0}</span></div>
        <div className="mod-row">
          {['STR','DEX','CON','INT','WIS','CHA'].map((s, i) => {
            const score = [p.strength, p.dexterity, p.constitution, p.intelligence, p.wisdom, p.charisma][i] || 10;
            const mod = Math.floor((score - 10) / 2);
            const cls = mod > 0 ? 'positive' : mod < 0 ? 'negative' : '';
            return <div key={s} className="mod-badge"><span className="mod-label">{s}</span><span className={`mod-val ${cls}`}>{mod >= 0 ? '+' : ''}{mod}</span></div>;
          })}
        </div>
        <div className="afflictions">{debuffIcons.length > 0 ? debuffIcons : ''}</div>
        {itemDetail && (
          <div className={`item-detail ${itemDetail.rarityClass}`}>
            <span className="item-detail-close" onClick={(e) => { e.stopPropagation(); setItemDetail(null); const svg = cardRef.current?.querySelector('.slot-connector'); if (svg) svg.remove(); }}>✕</span>
            <div className="item-name">{itemDetail.name.split(' ').map((w,i) => i === 0 ? w.charAt(0).toUpperCase() + w.slice(1) : ['of','the','a','an','and','in','on','to','for','from'].includes(w.toLowerCase()) ? w.toLowerCase() : w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}</div>
            <div className="item-type">{itemDetail.rarityLine}{itemDetail.typeLine ? ' · ' + itemDetail.typeLine : ''}</div>
            {itemDetail.acLine && <div className="item-stats">{itemDetail.acLine}</div>}
            {(itemDetail.statLines || []).map((s, i) => <div key={i} className="item-stat">{s}</div>)}
            {(itemDetail.passiveLines || []).length > 0 && <div className="item-passive">Equip: {itemDetail.passiveLines.join('. ')}</div>}
          </div>
        )}
      </div>
    </div>
  );
}
